import React from "react";

import _ from "lodash";

import { ReactComponent as ArrowDownBlack } from "commons/assets/icons/arrow_down_black.svg";

import { CheckErrorVisibilityInterface } from "commons/types/form";

import Tooltip from "commons/ui/Tooltip";

export type OptionType = {
    value: any;
    label: string;
    hidden?: boolean;
};

export type OnChangeInterface = (event: React.ChangeEvent<HTMLSelectElement>) => void;

export type PropsType = {
    [x: string]: any;
    label?: string;
    errors?: string[];
    onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    setValue?: (path: string, value: any) => void;
    hideError?: (path: string) => void;
    showError?: (path: string) => void;
    checkErrorVisibility?: CheckErrorVisibilityInterface;
    path?: string;
    options?: Array<OptionType>;
    optionsGrouped?: { [x: string]: Array<OptionType> };
    value?: any;
    tooltip?: any;
    disabled?: boolean;
    tabIndex?: number;
    autoFocus?: boolean;
    filterInput?: boolean;
    showPlaceholder?: boolean;
    required?: boolean;
    dontShowObligatoryFlag?: boolean;
    horizontalLabel?: boolean;
};

const calculateClasses = (props: PropsType, focused: boolean): Array<string> => {
    const classes = ["input"];

    if (props.errors && props.errors.length > 0) {
        classes.push("has-error");
    }

    if (focused) {
        classes.push("focused");
    }

    if (props.filterInput && (props.value || props.value === false)) {
        classes.push("not-empty");
    }

    if (props.showPlaceholder) {
        classes.push("placeholder-active");
    }

    if (props.value) {
        classes.push("has-value");
    }

    if (props.className) {
        classes.push(props.className);
    }

    return classes;
};

function Select(props: PropsType) {
    const [focused, setFocused] = React.useState(false);

    const errors: Array<string> = [];

    if (props.checkErrorVisibility && props.path) {
        props.checkErrorVisibility(props.path).forEach((error) => errors.push(error));
    } else if (_.isArray(props.errors)) {
        props.errors.forEach((error) => errors.push(error));
    }

    const classes = calculateClasses({ ...props, errors }, focused);

    const { onFocus, hideError, onBlur, showError, onChange, setValue, path } = props;

    const onFocusCalculated = React.useCallback(
        (event: React.FocusEvent<HTMLSelectElement>) => {
            setFocused(true);

            if (onFocus) {
                onFocus(event);
            } else if (hideError && path) {
                hideError(path);
            }
        },
        [onFocus, hideError, path]
    );

    const onBlurCalculated = React.useCallback(
        (event: React.FocusEvent<HTMLSelectElement>) => {
            setFocused(false);

            if (onBlur) {
                onBlur(event);
            } else if (showError && path) {
                showError(path);
            }
        },
        [onBlur, showError, path]
    );

    const onChangeCalculated = React.useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            if (onChange) {
                onChange(event);
            } else if (setValue && path) {
                setValue(path, event.target.value);
            }
        },
        [onChange, setValue, path]
    );

    const errorsId: string = React.useMemo(() => {
        if (props.label) {
            return `${props.label.toLocaleLowerCase().replace(/\s/g, "")}-select-errors`;
        }

        return `${_.uniqueId()}-select-errors`;
    }, [props.label]);

    return (
        <div className={`${classes.join(" ")} ${props.horizontalLabel ? "horizontal-input" : ""}`}>
            <div className={"label-container"}>
                {props.label && (
                    <div>
                        <label>
                            {props.label}
                            {props.required && !props.dontShowObligatoryFlag ? (
                                <span aria-describedby={"obligatory-fields-info-description"}>
                                    *
                                </span>
                            ) : null}
                        </label>
                    </div>
                )}

                {props.tooltip && <Tooltip tooltip={props.tooltip} />}
            </div>

            <div className={" input-container " + (props.disabled ? " disabled " : "")}>
                <select
                    aria-label={props.label ?? ""}
                    data-testid={"select-input"}
                    onFocus={onFocusCalculated}
                    onBlur={onBlurCalculated}
                    onChange={onChangeCalculated}
                    value={props.value}
                    disabled={props.disabled}
                    tabIndex={props.tabIndex}
                    autoFocus={props.autoFocus}
                    aria-required={props.required ? true : undefined}
                    aria-invalid={errors && errors.length > 0 ? true : undefined}
                    aria-describedby={errorsId}
                >
                    {props.options?.map((option) => (
                        <option
                            key={option.value + option.label}
                            value={option.value}
                            disabled={option.hidden}
                            hidden={option.hidden}
                        >
                            {option.label}
                        </option>
                    ))}

                    {_.map(props.optionsGrouped, (optionList, key) => (
                        <optgroup key={key.replace(/\s/g, "")} label={key}>
                            {optionList.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>

                <div className={"select-arrow"}>
                    <ArrowDownBlack />
                </div>
            </div>

            <span id={errorsId} className={"errors"}>
                {errors ? errors.join(", ") : ""}
            </span>
        </div>
    );
}

export default Select;
